import React, { memo, Fragment } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { Provider as ReduxProvider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { StaticRouter, BrowserRouter } from 'react-router-dom';

import { SUPPORTED_LOCALES } from 'utils';

import App from 'components/App';
import { DeviceDetectProvider } from 'providers';

const Root = memo(
  ({
    store,
    locale,
    messages,
    routerBasename,
    routerContext,
    helmetContext,
    routerLocation,
    env,
  }) => {
    return (
      <ReduxProvider store={store}>
        <IntlProvider
          locale={locale}
          defaultLocale={locale}
          messages={messages}
          textComponent={Fragment}
        >
          <DeviceDetectProvider>
            {env === 'server' ? (
              <HelmetProvider context={helmetContext}>
                <StaticRouter
                  location={routerLocation}
                  context={routerContext}
                  basename={routerBasename}
                >
                  <App />
                </StaticRouter>
              </HelmetProvider>
            ) : (
              <HelmetProvider>
                <BrowserRouter basename={routerBasename}>
                  <App />
                </BrowserRouter>
              </HelmetProvider>
            )}
          </DeviceDetectProvider>
        </IntlProvider>
      </ReduxProvider>
    );
  }
);

Root.propTypes = {
  locale: PropTypes.oneOf(SUPPORTED_LOCALES).isRequired,
  messages: PropTypes.objectOf(PropTypes.string).isRequired,
  store: PropTypes.objectOf(PropTypes.any).isRequired,
  routerBasename: PropTypes.string.isRequired,
  routerContext({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'object')
    ) {
      return new Error('Please provide routerContext!');
    }

    return null;
  },
  routerLocation({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'string')
    ) {
      return new Error('Please provide routerLocation!');
    }

    return null;
  },
  helmetContext({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'string')
    ) {
      return new Error('Please provide helmetContext!');
    }

    return null;
  },
  env: PropTypes.oneOf(['client', 'server']).isRequired,
};

Root.defaultProps = {
  routerContext: null,
  routerLocation: null,
  helmetContext: {},
};

export default Root;
